import { useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "@cloudscape-design/components/icon";

function ToolCard({ toolInvocation }) {
  const { t } = useTranslation();
  const [inputExpanded, setInputExpanded] = useState(true);
  const [outputExpanded, setOutputExpanded] = useState(true);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    try {
      // Handle Unix epoch (seconds) - multiply by 1000 for milliseconds
      const ts = typeof timestamp === "number" || /^\d+$/.test(timestamp) 
        ? Number(timestamp) * 1000 
        : timestamp;
      return new Date(ts).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
    } catch { return ""; }
  };

  const formatKeyName = (key) => {
    return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr + "T00:00:00");
      return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    } catch { return dateStr; }
  };

  const formatInputValue = (key, value) => {
    if (value === null || value === undefined) return "null";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object") return JSON.stringify(value);
    // Detect date strings (YYYY-MM-DD format)
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return formatDate(value);
    }
    return String(value);
  };

  const renderHumanReadable = (data, extractResult = false) => {
    if (!data) return null;
    // If it's a string, try to parse it as JSON
    let obj = data;
    if (typeof data === "string") {
      try {
        obj = JSON.parse(data);
      } catch {
        // Not JSON, return as plain text
        return <div className="tool-input-row"><span className="tool-input-value">{data}</span></div>;
      }
    }
    if (typeof obj !== "object" || obj === null) {
      return <div className="tool-input-row"><span className="tool-input-value">{String(obj)}</span></div>;
    }
    // Extract only the "result" object if requested
    if (extractResult && obj.result !== undefined) {
      obj = obj.result;
      if (typeof obj !== "object" || obj === null) {
        return <div className="tool-input-row"><span className="tool-input-value">{String(obj)}</span></div>;
      }
      // Further extract "data" object if present in result
      if (obj.data !== undefined) {
        obj = obj.data;
        if (typeof obj !== "object" || obj === null) {
          return <div className="tool-input-row"><span className="tool-input-value">{String(obj)}</span></div>;
        }
      }
    }
    return Object.entries(obj).map(([key, value]) => (
      <div key={key} className="tool-input-row">
        <span className="tool-input-label">{formatKeyName(key)}:</span>
        <span className="tool-input-value">{formatInputValue(key, value)}</span>
      </div>
    ));
  };

  const getStatusIcon = () => {
    const icons = { success: "status-positive", error: "status-negative", pending: "status-in-progress" };
    return <div className={`tool-card-status ${toolInvocation.status}`}><Icon name={icons[toolInvocation.status] || icons.pending} /></div>;
  };

  const hasInput = toolInvocation.input && typeof toolInvocation.input === "object" && Object.keys(toolInvocation.input).length > 0;

  return (
    <article className="tool-card">
      <div className="tool-card-header">
        <div className="tool-card-title">
          <Icon name="settings" />
          <span className="tool-card-name">{formatKeyName(toolInvocation.tool_name)}</span>
          <span className="tool-card-timestamp">{formatTimestamp(toolInvocation.timestamp)}</span>
        </div>
        {getStatusIcon()}
      </div>

      {hasInput && (
        <div className="tool-card-section">
          <div className="tool-card-section-header" onClick={() => setInputExpanded(!inputExpanded)} role="button" tabIndex={0}>
            <span className="tool-card-section-title">{t("conversation.tools.input")}</span>
            <Icon name={inputExpanded ? "angle-down" : "angle-right"} />
          </div>
          {inputExpanded && <div className="tool-card-section-content tool-input-list">{renderHumanReadable(toolInvocation.input)}</div>}
        </div>
      )}

      {(toolInvocation.output !== undefined || toolInvocation.error) && (
        <div className="tool-card-section">
          <div className="tool-card-section-header" onClick={() => setOutputExpanded(!outputExpanded)} role="button" tabIndex={0}>
            <span className="tool-card-section-title">{t("conversation.tools.output")}</span>
            <Icon name={outputExpanded ? "angle-down" : "angle-right"} />
          </div>
          {outputExpanded && (
            <div className="tool-card-section-content tool-input-list">
              {toolInvocation.error 
                ? <div className="tool-input-row"><span className="tool-input-value tool-error">{toolInvocation.error}</span></div>
                : renderHumanReadable(toolInvocation.output, true)
              }
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export default ToolCard;
